import { brokerExecutionModels } from '../data';

const modelTheme = [
  { bar: 'bg-secondary-500', text: 'text-secondary-700', bg: 'bg-secondary-50', border: 'border-secondary-200' },
  { bar: 'bg-primary-500', text: 'text-primary-700', bg: 'bg-primary-50', border: 'border-primary-200' },
  { bar: 'bg-accent-500', text: 'text-accent-700', bg: 'bg-accent-50', border: 'border-accent-200' },
];

const flowEndpoints = [
  ['Bank A', 'Bank B', 'Hedge Fund C'],
  ['LP 1', 'LP 2'],
  ['Broker\'s book'],
];

export default function BrokerExecutionSection() {
  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Editorial header */}
        <div className="max-w-[680px] mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[38px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            Where does your order go after you click &ldquo;buy&rdquo;?
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            The answer determines whether slippage is a neutral market fact or a hidden profit
            centre for your broker. Most retail traders never ask — and the difference can be
            thousands per year.
          </p>
        </div>

        {/* ── Flow diagrams: compact 3-column visual comparison ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t-2 border-foreground-950">
          {brokerExecutionModels.map((m, i) => {
            const th = modelTheme[i];
            const eps = flowEndpoints[i];
            return (
              <div
                key={m.model}
                className={`pt-8 pb-6 ${
                  i < 2 ? 'md:border-r border-background-200 md:pr-6 border-b md:border-b-0' : 'md:pl-6'
                }`}
              >
                <div className="flex flex-col items-center gap-0">
                  {/* Trader */}
                  <div className="w-full flex justify-center mb-2">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-background-100 border border-background-300 text-[12px] font-semibold text-foreground-600">
                      <i className="ri-user-3-line mr-1.5 text-[13px]" />
                      You
                    </span>
                  </div>

                  {/* Down arrow */}
                  <div className="flex flex-col items-center mb-2">
                    <div className="w-px h-5 bg-foreground-300" />
                    <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent border-t-foreground-300" />
                  </div>

                  {/* Broker model */}
                  <div className={`w-full rounded-lg border-2 ${th.border} ${th.bg} px-4 py-3 text-center mb-2`}>
                    <span className="block text-[15px] font-[750] text-foreground-950">{m.model}</span>
                  </div>

                  {/* Branching arrows */}
                  {eps.length > 2 ? (
                    <div className="flex flex-col items-center mb-2">
                      <div className="w-px h-4 bg-foreground-300" />
                      <div className="relative w-[120px] h-4">
                        <div className="absolute left-0 top-0 w-px h-4 bg-foreground-300 rotate-[25deg] origin-bottom-left" />
                        <div className="absolute left-1/2 top-0 w-px h-4 bg-foreground-300 -translate-x-1/2" />
                        <div className="absolute right-0 top-0 w-px h-4 bg-foreground-300 -rotate-[25deg] origin-bottom-right" />
                      </div>
                    </div>
                  ) : eps.length > 1 ? (
                    <div className="flex flex-col items-center mb-2">
                      <div className="w-px h-4 bg-foreground-300" />
                      <div className="relative w-[80px] h-4">
                        <div className="absolute left-0 top-0 w-px h-4 bg-foreground-300 rotate-[25deg] origin-bottom-left" />
                        <div className="absolute right-0 top-0 w-px h-4 bg-foreground-300 -rotate-[25deg] origin-bottom-right" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center mb-2">
                      <div className="w-px h-4 bg-foreground-300" />
                    </div>
                  )}

                  {/* Destinations */}
                  <div className="flex flex-wrap justify-center gap-2 mt-1">
                    {eps.map((ep) => (
                      <span
                        key={ep}
                        className="inline-flex items-center px-2.5 py-1 rounded-full bg-background-100 border border-background-300 text-[11px] font-semibold text-foreground-500"
                      >
                        {ep}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Model details: full-width stacked sections ── */}
        <div className="mt-2 divide-y divide-background-200 border-b border-background-200">
          {brokerExecutionModels.map((m, i) => {
            const th = modelTheme[i];
            return (
              <div key={m.model} className="py-10 md:py-12 first:pt-8">
                <div className="md:grid md:grid-cols-[180px_1fr] md:gap-10 lg:gap-14">
                  {/* Left: model label */}
                  <div className="mb-4 md:mb-0">
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${th.bar}`} />
                      <span className="text-[15px] font-[750] text-foreground-950">{m.model}</span>
                    </span>
                  </div>

                  {/* Right: content area */}
                  <div className="max-w-[640px]">
                    {/* Verdict */}
                    <p className="text-[16px] font-[650] text-foreground-900 leading-[1.55] mb-4">
                      {m.verdict}
                    </p>

                    {/* Body paragraphs */}
                    <div className="text-[14px] leading-[1.75] text-foreground-600 space-y-3 mb-6">
                      {m.bodyParagraphs.map((p, idx) => (
                        <p key={idx}>{p}</p>
                      ))}
                    </div>

                    {/* Specs */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-background-100/70 rounded-lg px-4 py-3">
                        <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-[0.09em] block mb-1">
                          Slippage stance
                        </span>
                        <span className="text-[13px] font-semibold text-foreground-800 leading-snug">
                          {m.slippageStance}
                        </span>
                      </div>
                      <div className="bg-background-100/70 rounded-lg px-4 py-3">
                        <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-[0.09em] block mb-1">
                          Positive slippage
                        </span>
                        <span className="text-[13px] font-semibold text-foreground-800 leading-snug">
                          {m.positiveSlippage}
                        </span>
                      </div>
                      <div className="bg-background-100/70 rounded-lg px-4 py-3">
                        <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-[0.09em] block mb-1">
                          Commission
                        </span>
                        <span className="text-[13px] font-semibold text-foreground-800 leading-snug">
                          {m.commission}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── How to audit your broker ── */}
        <div className="mt-14 md:mt-16 pt-10 border-t-2 border-foreground-950">
          <h3 className="text-[20px] font-[750] text-foreground-950 tracking-tight mb-2">
            How to check what execution model you actually have
          </h3>
          <p className="text-[14px] text-foreground-500 mb-8 max-w-[600px]">
            Your broker&apos;s marketing page may say &ldquo;ECN&rdquo; — but the trade execution
            statement tells the real story. Here is how to verify.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Questions to ask */}
            <div>
              <h4 className="text-[14px] font-bold text-foreground-950 uppercase tracking-[0.1em] mb-5">
                Ask your broker
              </h4>
              <ul className="space-y-5">
                {[
                  {
                    q: 'Do you operate a dealing desk?',
                    a: 'If yes, the broker is your counterparty. If no, orders go to the interbank market.',
                  },
                  {
                    q: 'Do you pass through positive slippage?',
                    a: 'The answer should be yes. If the broker keeps price improvements, they profit from your order flow.',
                  },
                  {
                    q: 'Can I see my order execution policy?',
                    a: 'Regulated brokers must publish this. It details slippage handling, requote policy, and execution venues.',
                  },
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-background-100 border border-background-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[12px] font-bold text-foreground-500">{idx + 1}</span>
                    </span>
                    <div>
                      <p className="text-[15px] font-semibold text-foreground-800 leading-snug">
                        &ldquo;{item.q}&rdquo;
                      </p>
                      <p className="text-[13px] text-foreground-500 mt-1 leading-relaxed">{item.a}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Red flags */}
            <div>
              <h4 className="text-[14px] font-bold text-foreground-950 uppercase tracking-[0.1em] mb-5">
                Red flags
              </h4>
              <ul className="space-y-5">
                {[
                  {
                    label: 'Consistently negative slippage',
                    detail: 'If 100% of your slippage is negative, the broker is likely capturing the positive side. Real markets produce both.',
                  },
                  {
                    label: 'Frequent requotes',
                    detail: 'A requote means the broker rejected your price and offers a new one — usually worse. ECN brokers almost never requote.',
                  },
                  {
                    label: 'Slippage spikes during calm markets',
                    detail: 'If EURUSD slips 3 pips during London, something is wrong. Under normal conditions, majors should slip less than 0.5 pips.',
                  },
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-accent-100 border border-accent-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-[13px] text-accent-600" />
                    </span>
                    <div>
                      <p className="text-[14px] font-semibold text-foreground-800 leading-snug">{item.label}</p>
                      <p className="text-[13px] text-foreground-500 mt-1 leading-relaxed">{item.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}